import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Plus, Search, Edit, Trash2 } from "lucide-react";

type MembershipStatus = "Premium" | "Regular" | "Inactive";

interface Client {
  id: number;
  name: string;
  email: string;
  phone: string;
  membershipStatus: MembershipStatus;
  joinDate: string;
  subscriptionType: string;
  hoursLeft: number;
}

export function ClientManagement() {
  const [clients, setClients] = useState<Client[]>([
    {
      id: 1,
      name: "John Doe",
      email: "john.doe@email.com",
      phone: "+1 234 567 8900",
      membershipStatus: "Premium",
      joinDate: "2024-01-15",
      subscriptionType: "Monthly Premium",
      hoursLeft: 45,
    },
    {
      id: 2,
      name: "Sarah Wilson",
      email: "sarah.wilson@email.com",
      phone: "+1 234 567 8901",
      membershipStatus: "Regular",
      joinDate: "2024-02-20",
      subscriptionType: "30-hour package",
      hoursLeft: 22,
    },
    {
      id: 3,
      name: "Mike Johnson",
      email: "mike.johnson@email.com",
      phone: "+1 234 567 8902",
      membershipStatus: "Premium",
      joinDate: "2024-01-10",
      subscriptionType: "Monthly Premium",
      hoursLeft: 38,
    },
    {
      id: 4,
      name: "Emma Brown",
      email: "emma.brown@email.com",
      phone: "+1 234 567 8903",
      membershipStatus: "Regular",
      joinDate: "2024-03-05",
      subscriptionType: "10-hour package",
      hoursLeft: 8,
    },
    {
      id: 5,
      name: "Alex Turner",
      email: "alex.turner@email.com",
      phone: "+1 234 567 8904",
      membershipStatus: "Inactive",
      joinDate: "2023-12-01",
      subscriptionType: "Expired",
      hoursLeft: 0,
    },
  ]);

  const [searchTerm, setSearchTerm] = useState("");
  const [newClient, setNewClient] = useState({
    name: "",
    email: "",
    phone: "",
    membershipStatus: "Regular" as MembershipStatus,
  });

  const filteredClients = clients.filter(
    (client) =>
      client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddClient = () => {
    const client: Client = {
      id: Math.max(...clients.map((c) => c.id)) + 1,
      ...newClient,
      joinDate: new Date().toISOString().split("T")[0],
      subscriptionType: "None",
      hoursLeft: 0,
    };
    setClients([...clients, client]);
    setNewClient({
      name: "",
      email: "",
      phone: "",
      membershipStatus: "Regular",
    });
  };

  const handleDeleteClient = (id: number) => {
    setClients(clients.filter((client) => client.id !== id));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Premium":
        return "bg-purple-100 text-purple-800";
      case "Regular":
        return "bg-blue-100 text-blue-800";
      case "Inactive":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with Add Client Button */}
      <div className="flex justify-between items-center">
        <div>
          <h2>Client Management</h2>
          <p className="text-muted-foreground">
            Manage your coworking space clients
          </p>
        </div>
        <Dialog>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add Client
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add New Client</DialogTitle>
              <DialogDescription>
                Enter the client details below.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Full Name</Label>
                <Input
                  id="name"
                  value={newClient.name}
                  onChange={(e) =>
                    setNewClient({ ...newClient, name: e.target.value })
                  }
                  placeholder="Enter full name"
                />
              </div>
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newClient.email}
                  onChange={(e) =>
                    setNewClient({ ...newClient, email: e.target.value })
                  }
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={newClient.phone}
                  onChange={(e) =>
                    setNewClient({ ...newClient, phone: e.target.value })
                  }
                  placeholder="Enter phone number"
                />
              </div>
              <div>
                <Label htmlFor="membership">Membership Status</Label>
                <Select
                  value={newClient.membershipStatus}
                  onValueChange={(value: MembershipStatus) =>
                    setNewClient({ ...newClient, membershipStatus: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Regular">Regular</SelectItem>
                    <SelectItem value="Premium">Premium</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleAddClient} className="w-full">
                Add Client
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle>Search Clients</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
        </CardContent>
      </Card>

      {/* Clients Table */}
      <Card>
        <CardHeader>
          <CardTitle>All Clients ({filteredClients.length})</CardTitle>
          <CardDescription>Overview of all registered clients</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Subscription</TableHead>
                <TableHead>Hours Left</TableHead>
                <TableHead>Join Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredClients.map((client) => (
                <TableRow key={client.id}>
                  <TableCell>{client.name}</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="text-sm">{client.email}</div>
                      <div className="text-sm text-muted-foreground">
                        {client.phone}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(client.membershipStatus)}>
                      {client.membershipStatus}
                    </Badge>
                  </TableCell>
                  <TableCell>{client.subscriptionType}</TableCell>
                  <TableCell>
                    <span
                      className={client.hoursLeft <= 5 ? "text-red-600" : ""}
                    >
                      {client.hoursLeft}
                    </span>
                  </TableCell>
                  <TableCell>{client.joinDate}</TableCell>
                  <TableCell>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDeleteClient(client.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
